import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, ButtonType, ButtonSize, IconName } from '@hills-design-system/components';
import InputText from '../InputText/InputText';
import ConfirmationModal from '../Modal/ConfirmationModal';
import Modal from '../Modal/Modal';
import { ModalSize } from '../Modal/Modal.types';
import Card from '../Card/Card';
import {
  fetchAllShelters,
  createShelter,
  updateShelter,
  deleteShelter,
  downloadShelterTemplate,
  uploadShelterSpreadsheet,
  bulkImportShelters,
  Shelter,
  CreateShelterDto,
  UpdateShelterDto,
} from '../../services/api';
import { useShelterState } from '../../hooks/useShelter';
import {
  convertUTCToLocalDate,
  dateToDateOnlyStringLocal,
  normalizeCalendarDateToDateOnlyString,
  parseDateOnlyStringToLocalDate,
} from '../../utils/dateUtils';

interface ShelterManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShelterFormData {
  name: string;
  sold_to: string;
  join_program_date: string | null;
}

interface UploadResult {
  success: boolean;
  processedCount: number;
  data: Array<{ name: string; sold_to: string; join_program_date: string }>;
  errors: Array<{ row: number; field: string; message: string }>;
}

const ShelterManagement: React.FC<ShelterManagementProps> = ({ isOpen, onClose }) => {
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingShelter, setEditingShelter] = useState<Shelter | null>(null);
  const [deletingShelterId, setDeletingShelterId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ShelterFormData>({
    name: '',
    sold_to: '',
    join_program_date: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importSuccess, setImportSuccess] = useState<{
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ index: number; sold_to: string; error: string }>;
  } | null>(null);
  const [shelterSearchText, setShelterSearchText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current shelter to prevent deletion
  const { shelter: currentShelter } = useShelterState();

  useEffect(() => {
    if (isOpen) {
      loadShelters();
    }
  }, [isOpen]);

  const loadShelters = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllShelters();
      setShelters(data);
    } catch (err) {
      console.error('Failed to load shelters:', err);
      setError('Failed to load shelters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingShelter(null);
    setFormData({
      name: '',
      sold_to: '',
      join_program_date: null,
    });
    setError(null); // Clear any previous errors
    setIsFormOpen(true);
  };

  const handleEdit = (shelter: Shelter) => {
    setEditingShelter(shelter);
    setFormData({
      name: shelter.name,
      sold_to: shelter.sold_to,
      join_program_date: normalizeCalendarDateToDateOnlyString(shelter.join_program_date),
    });
    setError(null); // Clear any previous errors
    setIsFormOpen(true);
  };

  const handleDelete = (shelterId: string) => {
    setDeletingShelterId(shelterId);
  };

  const confirmDelete = async () => {
    if (!deletingShelterId) return;

    setSubmitting(true);
    setError(null);
    try {
      await deleteShelter(deletingShelterId);
      await loadShelters();
      setDeletingShelterId(null);
    } catch (err) {
      console.error('Failed to delete shelter:', err);
      setError('Failed to delete shelter. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingShelter) {
        // Update existing shelter
        const updateData: UpdateShelterDto = {
          name: formData.name,
          sold_to: formData.sold_to,
          join_program_date: formData.join_program_date ?? undefined,
        };
        await updateShelter(editingShelter.id, updateData);
      } else {
        // Create new shelter
        if (!formData.join_program_date) {
          setError('Join program date is required');
          setSubmitting(false);
          return;
        }
        const createData: CreateShelterDto = {
          name: formData.name,
          sold_to: formData.sold_to,
          join_program_date: formData.join_program_date,
        };
        await createShelter(createData);
      }

      await loadShelters();
      setIsFormOpen(false);
      setFormData({ name: '', sold_to: '', join_program_date: null });
    } catch (err) {
      console.error('Failed to save shelter:', err);
      const errorMessage = getErrorMessage(err, 'Failed to save shelter. Please try again.');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadTemplate = async (format: 'csv' | 'xlsx') => {
    try {
      const blob = await downloadShelterTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shelters-template.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download template:', err);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadResult(null);
    setImportSuccess(null);

    try {
      const result = await uploadShelterSpreadsheet(file);
      setUploadResult(result);
      setIsUploadModalOpen(true);
    } catch (err) {
      console.error('Failed to upload file:', err);
      setError('Failed to process file. Please check the format and try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImportData = async (updateOnDuplicate: boolean) => {
    if (!uploadResult?.data) return;

    setSubmitting(true);
    setError(null);

    try {
      const sheltersToImport: CreateShelterDto[] = uploadResult.data.map((item) => ({
        name: item.name,
        sold_to: item.sold_to,
        join_program_date: item.join_program_date,
      }));

      const result = await bulkImportShelters(sheltersToImport, updateOnDuplicate);
      setImportSuccess({
        created: result.created,
        updated: result.updated,
        skipped: result.skipped,
        errors: result.errors || [],
      });

      await loadShelters();
    } catch (err) {
      console.error('Failed to import shelters:', err);
      const errorMessage = getErrorMessage(err, 'Failed to import shelters. Please try again.');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setUploadResult(null);
    setImportSuccess(null);
  };

  const formatDate = (date: string) => {
    const localDate =
      parseDateOnlyStringToLocalDate(date) ?? convertUTCToLocalDate(date) ?? new Date(date);
    const d = isNaN(localDate.getTime()) ? new Date(date) : localDate;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const ShelterListTable: React.FC<{ shelters: Shelter[] }> = ({ shelters }) => (
    <div className="overflow-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Sold To
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Join Date
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {shelters.map((shelter) => (
            <tr key={shelter.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {shelter.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {shelter.sold_to}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(shelter.join_program_date)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => handleEdit(shelter)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(shelter.id)}
                    disabled={currentShelter?.id === shelter.id}
                    className={
                      currentShelter?.id === shelter.id
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-red-600 hover:text-red-900'
                    }
                    title={
                      currentShelter?.id === shelter.id
                        ? 'Cannot delete your current shelter'
                        : 'Delete shelter'
                    }
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const NoShelterFound: React.FC = () => (
    <div className="text-center py-12 text-gray-500">
      <p>No shelters match your search.</p>
    </div>
  );

  const ShelterUploadValidationErrors: React.FC<{ uploadResult: UploadResult }> = ({
    uploadResult,
  }) => (
    <div className="flex-1 overflow-y-auto">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-3">
        <h4 className="font-semibold text-orange-900 mb-1">Validation Errors</h4>
        <p className="text-xs text-orange-700">Rows with errors will be skipped during import.</p>
      </div>
      <div className="space-y-2">
        {uploadResult.errors.map((error, index) => (
          <Card
            key={index}
            variant="outline"
            padding="small"
            radius="medium"
            className="border-orange-200 bg-orange-50/50"
          >
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 px-2 py-1 bg-orange-200 text-orange-900 text-xs font-semibold rounded">
                Row {error.row}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-orange-900">{error.field}</p>
                <p className="text-xs text-orange-700 mt-0.5">{error.message}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const ShelterUploadPreview: React.FC<{ uploadResult: UploadResult }> = ({ uploadResult }) => (
    <div className="flex-1 overflow-y-auto">
      <h4 className="font-semibold text-gray-900 mb-3">
        Valid Shelters ({uploadResult.data.length})
      </h4>
      <div className="space-y-2">
        {uploadResult.data.map((shelter, index) => (
          <Card
            key={index}
            variant="outline"
            padding="small"
            radius="medium"
            className="hover:border-blue-300 transition-colors"
          >
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{shelter.name}</p>
                <p className="text-xs text-gray-500">ID: {shelter.sold_to}</p>
              </div>
              <div className="text-xs text-gray-400">{formatDate(shelter.join_program_date)}</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
  /**
   * Extract user-friendly error message from API error response
   */
  const getErrorMessage = (err: unknown, defaultMessage: string): string => {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      const data = err.response?.data;

      // Handle 409 Conflict - Duplicate sold_to
      // Backend wraps the error in a message field due to global exception filter
      if (status === 409) {
        const errorData = typeof data?.message === 'object' ? data.message : data;
        if (errorData?.error === 'DUPLICATE_SOLD_TO') {
          return errorData.message ?? `A shelter with this Sold To ID already exists.`;
        }
      }

      // Handle other API errors with message
      if (data?.message) {
        return typeof data.message === 'string' ? data.message : defaultMessage;
      }
    }

    return defaultMessage;
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Shelter Management"
        size={ModalSize.Large}
        className="!h-[90vh]"
      >
        <div className="flex flex-col h-full space-y-4">
          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              label="Download Template"
              type={ButtonType.Secondary}
              size={ButtonSize.Medium}
              onClick={() => handleDownloadTemplate('csv')}
              icon={IconName.Download}
            />
            <Button
              label={uploading ? 'Uploading...' : 'Import CSV'}
              type={ButtonType.Secondary}
              size={ButtonSize.Medium}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              icon={IconName.Upload}
            />
            <Button
              label="Add New Shelter"
              type={ButtonType.Primary}
              size={ButtonSize.Medium}
              onClick={handleAddNew}
              icon={IconName.Plus}
            />
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Shelters List */}
          {loading ? (
            <div className="flex items-center justify-center flex-1">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : shelters.length === 0 ? (
            <div className="text-center flex-1 flex items-center justify-center text-gray-500">
              <p>No shelters found. Add your first shelter to get started.</p>
            </div>
          ) : (
            (() => {
              const filteredShelters = shelters.filter((shelter) => {
                const searchText = shelterSearchText.toLowerCase();
                return (
                  shelter.name.toLowerCase().includes(searchText) ||
                  shelter.sold_to.toLowerCase().includes(searchText)
                );
              });

              return (
                <div className="flex-1 overflow-auto flex flex-col">
                  {/* Search Input */}
                  <div className="mb-3 sticky top-0 bg-white z-10 pb-2">
                    <input
                      id="search-shelters"
                      type="text"
                      value={shelterSearchText}
                      onChange={(e) => setShelterSearchText(e.target.value)}
                      placeholder="Search by name or ID..."
                      className="w-full px-4 py-2.5 border border-black/20 rounded-lg focus:outline-none focus:border-blue-500 text-sm transition-colors"
                    />
                    {shelterSearchText && (
                      <p className="text-xs text-gray-600 mt-1.5">
                        Showing {filteredShelters.length} of {shelters.length} shelters
                      </p>
                    )}
                  </div>

                  {filteredShelters.length === 0 ? (
                    <NoShelterFound />
                  ) : (
                    <ShelterListTable shelters={filteredShelters} />
                  )}
                </div>
              );
            })()
          )}
        </div>
      </Modal>

      {/* Add/Edit Form Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setError(null); // Clear error when closing modal
        }}
        title={editingShelter ? 'Edit Shelter' : 'Add New Shelter'}
        size={ModalSize.Medium}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <InputText
            id="shelter-name"
            label="Shelter Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter shelter name"
            required
          />

          <InputText
            id="shelter-sold-to"
            label="Sold To"
            type="text"
            value={formData.sold_to}
            onChange={(e) => setFormData({ ...formData, sold_to: e.target.value })}
            placeholder="Enter sold_to identifier"
            required
          />

          <InputText
            id="shelter-join-date"
            label="Join Program Date"
            type="date"
            value={parseDateOnlyStringToLocalDate(formData.join_program_date)}
            onChange={(date) =>
              setFormData({
                ...formData,
                join_program_date: dateToDateOnlyStringLocal(date),
              })
            }
            placeholder="Select join program date"
            required
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button
              label="Cancel"
              type={ButtonType.Secondary}
              size={ButtonSize.Medium}
              onClick={() => {
                setIsFormOpen(false);
                setError(null); // Clear error when canceling
              }}
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingShelterId}
        onClose={() => setDeletingShelterId(null)}
        title="Delete Shelter"
        message="Are you sure you want to delete this shelter? This action cannot be undone and may affect access for users associated with this shelter."
        confirmLabel="Delete Shelter"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        variant="danger"
      />

      {/* Upload Results Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        title="Import Shelters"
        size={ModalSize.Large}
        className="!h-[90vh]"
      >
        <div className="flex flex-col h-full">
          {importSuccess ? (
            // Import Success Summary
            <div className="flex flex-col h-full justify-between space-y-4">
              <div className="flex-1 overflow-y-auto space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">
                    Import Completed Successfully!
                  </h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>✓ Created: {importSuccess.created} shelters</p>
                    {importSuccess.updated > 0 && (
                      <p>✓ Updated: {importSuccess.updated} shelters</p>
                    )}
                    {importSuccess.skipped > 0 && (
                      <p>⊘ Skipped: {importSuccess.skipped} duplicates</p>
                    )}
                  </div>
                </div>

                {/* Show duplicate/error details if any */}
                {importSuccess.errors.length > 0 && (
                  <div className="flex-1 overflow-y-auto">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-3">
                      <h4 className="font-semibold text-orange-900 mb-1">Skipped Items</h4>
                      <p className="text-xs text-orange-700">
                        The following shelters were skipped because they already exist.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {importSuccess.errors.map((error, index) => (
                        <Card
                          key={index}
                          variant="outline"
                          padding="small"
                          radius="medium"
                          className="border-orange-200 bg-orange-50/50"
                        >
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 px-2 py-1 bg-orange-200 text-orange-900 text-xs font-semibold rounded">
                              Row {error.index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-orange-900">
                                Sold To: {error.sold_to}
                              </p>
                              <p className="text-xs text-orange-700 mt-0.5">{error.error}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  label="Close"
                  type={ButtonType.Primary}
                  size={ButtonSize.Medium}
                  onClick={handleCloseUploadModal}
                />
              </div>
            </div>
          ) : uploadResult ? (
            // Upload Results and Import Options
            <div className="flex flex-col h-full space-y-4">
              <div className="flex-1 overflow-y-auto space-y-4">
                {/* Summary */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Upload Summary</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>Total rows processed: {uploadResult.processedCount}</p>
                    <p>Valid shelters: {uploadResult.data.length - uploadResult.errors.length}</p>
                    {uploadResult.errors.length > 0 && (
                      <p className="text-orange-700">
                        Rows with errors: {uploadResult.errors.length}
                      </p>
                    )}
                  </div>
                </div>

                {/* Validation Errors */}
                {uploadResult.errors.length > 0 && (
                  <ShelterUploadValidationErrors uploadResult={uploadResult} />
                )}

                {/* Valid Data Preview */}
                {uploadResult.data.length > 0 && (
                  <ShelterUploadPreview uploadResult={uploadResult} />
                )}
              </div>

              {/* Import Actions */}
              <div className="flex flex-col gap-3 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  How would you like to handle duplicates (shelters with existing sold_to)?
                </p>
                <div className="flex gap-3 justify-end">
                  <Button
                    label="Cancel"
                    type={ButtonType.Secondary}
                    size={ButtonSize.Medium}
                    onClick={handleCloseUploadModal}
                    disabled={submitting}
                  />
                  <Button
                    label={submitting ? 'Importing...' : 'Skip Duplicates'}
                    type={ButtonType.Secondary}
                    size={ButtonSize.Medium}
                    onClick={() => handleImportData(false)}
                    disabled={submitting || uploadResult.data.length === 0}
                  />
                  <Button
                    label={submitting ? 'Importing...' : 'Update Duplicates'}
                    type={ButtonType.Primary}
                    size={ButtonSize.Medium}
                    onClick={() => handleImportData(true)}
                    disabled={submitting || uploadResult.data.length === 0}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default ShelterManagement;
